
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { TreeNode } from "@/types/tree";
import { documentStore, uiStore } from "@/store/store";
import {
  calcBackdropCutoutSize,
  startBackdropListeners
} from "@/lib/inline-editor/backdrop";
import {
  wrapEditorInSsrNode,
  getSsrNodePlaceholders,
  createSsrPlaceholders
} from "@/lib/inline-editor/ssr-utils";
import { scrollToActiveElement } from "@/lib/canvas/scroll-to-active-element";

@Component({
  components: {
    BuilderElement: () => import("@/components/Canvas/BuilderElement.vue")
  }
})
export default class InlineEditor extends Vue {
  @Prop()
  element!: TreeNode;

  @Prop()
  ssrTemplate!: string | null;

  saving = false;
  resizeObserver: ResizeObserver | null = null;
  blockId?: number | null = null;
  nodes: TreeNode[] = [];

  unsubscribe: () => void = () => {};

  @Watch("element", { deep: true })
  onElementChange() {
    if (!this.context?.showBackdrop) return;
    calcBackdropCutoutSize(this.$refs.container as HTMLElement);
  }

  get context() {
    return uiStore.inlineEditingContext;
  }

  get postId() {
    return documentStore.document.documentMeta.id;
  }

  get selector() {
    return `[data-inline-editable-node="${this.postId}-${this.element.id}"]`;
  }

  get ssrNodePlaceholders() {
    return getSsrNodePlaceholders(this.ssrTemplate, this.selector);
  }

  async mounted() {
    if (this.context?.showBackdrop) {
      this.unsubscribe = startBackdropListeners(
        this.$refs.container as HTMLElement
      );
    }

    if (this.context?.scrollToElementOnStart) {
      this.$nextTick(() => scrollToActiveElement());
    }
  }

  async beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  render() {
    const loader = (
      <div class="breakdance-inline-editor__loader">Loading Block...</div>
    );

    const editor = (
      <div class="breakdance-inline-editor" ref="container">
        {this.context?.loading ? loader : this.$slots.default}
      </div>
    );

    const placeholders = createSsrPlaceholders(this.ssrNodePlaceholders);

    // Support for Post Loop Builders
    if (this.ssrTemplate) {
      return wrapEditorInSsrNode(
        editor,
        placeholders,
        this.ssrTemplate,
        this.selector
      );
    }

    return editor;
  }
}
